import React, { useEffect, useState } from "react";
import { api, type SessionUser } from "../api/client";

interface Note {
  id: string;
  content: string;
  resolved: number;
  discord_username: string;
  avatar_url: string | null;
  lang_code: string | null;
  created_at: string;
}

export function NotesPanel({
  projectId,
  cardId,
  lang,
  user,
}: {
  projectId: string;
  cardId: string;
  lang: string | null;
  user: SessionUser | null;
}) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [content, setContent] = useState("");
  const [onlyThisLang, setOnlyThisLang] = useState(false);
  const canWrite = !!user && (user.role === "admin" || (user.isGuildMember && user.hasRequiredRole));

  async function reload() {
    const r = await api.listNotes(projectId, cardId, onlyThisLang ? lang ?? undefined : undefined);
    setNotes(r.notes);
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, cardId, onlyThisLang, lang]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!content.trim()) return;
    await api.addNote(projectId, cardId, { content: content.trim(), langCode: lang ?? undefined });
    setContent("");
    reload();
  }

  return (
    <aside className="notes-panel">
      <div className="notes-panel__header">
        <h4>Notes</h4>
        {lang && (
          <label className="notes-panel__filter">
            <input type="checkbox" checked={onlyThisLang} onChange={(e) => setOnlyThisLang(e.target.checked)} />
            Uniquement pour « {lang} »
          </label>
        )}
      </div>

      <ul className="notes-panel__list">
        {notes.map((n) => (
          <li key={n.id} className={n.resolved ? "resolved" : ""}>
            <div className="notes-panel__author">
              {n.avatar_url && <img src={n.avatar_url} alt="" />}
              <strong>{n.discord_username}</strong>
              {n.lang_code && <span className="badge">{n.lang_code}</span>}
            </div>
            <p>{n.content}</p>
            {canWrite && (
              <button
                className="btn btn--ghost btn--small"
                onClick={async () => {
                  await api.resolveNote(n.id, !n.resolved);
                  reload();
                }}
              >
                {n.resolved ? "Rouvrir" : "Marquer résolue"}
              </button>
            )}
          </li>
        ))}
        {notes.length === 0 && <p className="notes-panel__empty">Aucune note pour cette carte.</p>}
      </ul>

      {canWrite ? (
        <form onSubmit={handleSubmit} className="notes-panel__form">
          <textarea
            className="input"
            placeholder={lang ? `Ajouter une note sur la traduction « ${lang} »…` : "Ajouter une note…"}
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          <button className="btn btn--primary btn--small" type="submit">
            Publier
          </button>
        </form>
      ) : (
        <p className="notes-panel__login-hint">
          Connecte-toi avec un compte Discord membre du serveur pour ajouter une note.
        </p>
      )}
    </aside>
  );
}
